import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * In-place sede of a casa central landing (beauty, canchas and reservas share it).
 * The chosen sede lives in `?sucursal=<slug|id>`; an unknown value is the casa central itself.
 *
 * `fetchDetail(ref)` loads one venue's detail already normalized, or returns null when the
 * response has no venue. Details are cached for the life of the page (id -> entry).
 *
 * Returns:
 * - `sedes`: what the Sucursales section and the panel picker take (null without branches);
 * - `entry`: the cached detail of the active sede (`{ status: "ok", data }` or an error), null on the casa;
 * - `status`: while the sede loads or failed, what the swapped area shows; null otherwise.
 */
export function useSedeActiva({ casa, sucursales, fetchDetail }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const param = searchParams.get("sucursal");

  const activeItem = useMemo(() => {
    const ref = String(param || "").trim().toLowerCase();
    if (!ref) return null;
    return sucursales?.find((s) => String(s.slug || "").toLowerCase() === ref || String(s._id).toLowerCase() === ref) || null;
  }, [sucursales, param]);
  const activeId = activeItem ? String(activeItem._id) : null;

  const fetchRef = useRef(fetchDetail);
  fetchRef.current = fetchDetail;
  const cache = useRef({});
  const [, bump] = useReducer((n) => n + 1, 0);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!activeItem || cache.current[activeId]) return undefined;
    let active = true;
    const load = async () => {
      try {
        let detail;
        try {
          detail = await fetchRef.current(activeItem.slug || activeItem._id);
        } catch (err) {
          if (!activeItem.slug) throw err;
          detail = await fetchRef.current(activeItem._id);
        }
        cache.current[activeId] = detail
          ? { status: "ok", data: detail }
          : { status: "error", message: `No pudimos cargar ${activeItem.name}.` };
      } catch (err) {
        cache.current[activeId] = {
          status: "error",
          message: err?.response?.data?.message || `No pudimos cargar ${activeItem.name}.`,
        };
      }
      if (active) bump();
    };
    load();
    return () => { active = false; };
  }, [activeItem, activeId, reload]);

  const retry = useCallback(() => {
    delete cache.current[activeId];
    setReload((k) => k + 1);
    bump();
  }, [activeId]);

  // Push (not replace): each pick is a step, so Back returns to the previous sede and Forward undoes it.
  const onSelect = useCallback(
    (item) => {
      const next = new URLSearchParams(searchParams);
      if (!item || String(item._id) === String(casa?._id)) next.delete("sucursal");
      else next.set("sucursal", item.slug || String(item._id));
      // Keep the searched date (location.state) so the new sede opens on the same day.
      setSearchParams(next, { state: location.state });
    },
    [searchParams, setSearchParams, casa, location.state],
  );

  const sedes = casa && sucursales?.length ? { casa, activeId: activeId || String(casa._id), onSelect } : null;
  const entry = activeItem ? cache.current[activeId] || null : null;
  const status =
    activeItem && entry?.status !== "ok"
      ? { status: entry ? "error" : "loading", message: entry?.message, name: activeItem.name, onRetry: retry }
      : null;

  return { sedes, entry: entry?.status === "ok" ? entry : null, status };
}
